import {
    bexHttpClient
} from '../../clients';
import {
    CreateJSONOpcodeRequest,
    CreateJSONOpcodeResponse,
    GetJSONOpcodeByIdQuery,
    GetJSONOpcodeByIdResponse,
    ListJSONOpcodesQuery,
    ListJSONOpcodesResponse,
    UpdateJSONOpcodeRequest,
    UpdateJSONOpcodeResponse,
    DeleteJSONOpcodesRequest,
    DeleteJSONOpcodesResponse
} from './jsonOpcode.service.types';
import {
 normalizeToV2PlusWire
} from './opcodeWireNormalizer';

/**
 * Service class for performing JSON Opcode (v2) operations.
 */
class JSONOpCodeService {
    /**
     * Creates a new JSON Opcode.
     * @param {CreateJSONOpcodeRequest} data - The data to create a new JSON Opcode.
     * @returns {Promise<CreateJSONOpcodeResponse>} The response from the server regarding JSON Opcode creation.
     */
    async createJSONOpcode(data: CreateJSONOpcodeRequest): Promise<CreateJSONOpcodeResponse> {
        // A-0008.03: normalize legacy UI state -> V2+ wire shape on every save.
        const payload = {
 ...data,
opcode: normalizeToV2PlusWire(data.opcode)
};
        const response = await bexHttpClient.post('/redis/opcodes/opcode/create', payload);
        return response;
    }

    /**
     * Gets a JSON Opcode by ID.
     * @param {GetJSONOpcodeByIdQuery} params - Query parameters containing organizationId, subOrganizationId, and opcodeId.
     * @returns {Promise<GetJSONOpcodeByIdResponse>} The response containing the JSON Opcode.
     */
    async getJSONOpcodeById(params: GetJSONOpcodeByIdQuery): Promise<GetJSONOpcodeByIdResponse> {
        const {
            organizationId, subOrganizationId, opcodeId
        } = params;
        const response = await bexHttpClient.get(
            `/redis/opcodes/opcode/get-by-id?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}&opcodeId=${opcodeId}`
        );
        return response;
    }

    /**
     * Lists all JSON Opcodes for an organization and sub-organization.
     * @param {ListJSONOpcodesQuery} params - Query parameters containing organizationId and subOrganizationId.
     * @returns {Promise<ListJSONOpcodesResponse>} The response containing the list of JSON Opcodes.
     */
    async listJSONOpcodes(params: ListJSONOpcodesQuery & { page?: number; limit?: number }): Promise<ListJSONOpcodesResponse> {
        const {
            organizationId, subOrganizationId, page, limit
        } = params;
        let url = `/redis/opcodes/opcode/list?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`;
        if (page !== undefined) url += `&page=${page}`;
        if (limit !== undefined) url += `&limit=${limit}`;
        const response = await bexHttpClient.get(url);
        return response;
    }

    /**
     * Updates an existing JSON Opcode.
     * @param {UpdateJSONOpcodeRequest} data - The updated data for the JSON Opcode.
     * @returns {Promise<UpdateJSONOpcodeResponse>} The response containing the updated JSON Opcode.
     */
    async updateJSONOpcode(data: UpdateJSONOpcodeRequest): Promise<UpdateJSONOpcodeResponse> {
        // A-0008.03: normalize legacy UI state -> V2+ wire shape on every save.
        const payload = {
 ...data,
opcode: normalizeToV2PlusWire(data.opcode)
};
        const response = await bexHttpClient.put('/redis/opcodes/opcode/update', payload);
        return response;
    }

    /**
     * Deletes JSON Opcodes by opcode IDs.
     * @param {DeleteJSONOpcodesRequest} data - The payload containing the list of opcode IDs to delete.
     * @returns {Promise<DeleteJSONOpcodesResponse>} The response confirming the deletion.
     */
    async deleteJSONOpcodes(data: DeleteJSONOpcodesRequest): Promise<DeleteJSONOpcodesResponse> {
        const response = await bexHttpClient.delete('/redis/opcodes/opcode/delete', {
            data
        });
        return response;
    }
}

const jsonOpcodeService = new JSONOpCodeService();
export {
    jsonOpcodeService
};
